import React from 'react';

/**
 * Adjusts contrast by stretching color channels around the midpoint.
 * @param {{ value?: number }} props
 * @param {number} [props.value=0] Contrast adjustment percentage.
 * @returns {JSX.Element}
 */
const ContrastFilter = ({ value = 0 }) => {
  const a = (value / 100 + 1) ** 2;
  return (
    <feComponentTransfer>
      <feFuncR type="linear" slope={a} intercept={-(0.5 * a) + 0.5} />
      <feFuncG type="linear" slope={a} intercept={-(0.5 * a) + 0.5} />
      <feFuncB type="linear" slope={a} intercept={-(0.5 * a) + 0.5} />
    </feComponentTransfer>
  );
};

/**
 * Scales brightness by multiplying each RGB channel with the same factor.
 * @param {{ value?: number }} props
 * @param {number} [props.value=0] Brightness adjustment percentage.
 * @returns {JSX.Element}
 */
const BrightenFilter = ({ value = 0 }) => {
  const a = value / 100 + 1;
  return (
    <feComponentTransfer>
      <feFuncR type="linear" slope={a} />
      <feFuncG type="linear" slope={a} />
      <feFuncB type="linear" slope={a} />
    </feComponentTransfer>
  );
};

/**
 * Converts the image to grayscale when active.
 * @param {{ active?: boolean }} props
 * @param {boolean} [props.active=false] Whether to apply the grayscale matrix.
 * @returns {JSX.Element|null}
 */
const BlackWhiteFilter = ({ active = false }) =>
  active ? (
    <feColorMatrix
      type="matrix"
      values="
        0.2126 0.7152 0.0722 0 0
        0.2126 0.7152 0.0722 0 0
        0.2126 0.7152 0.0722 0 0
        0       0      0     1 0
      "
    />
  ) : null;

/**
 * Adjusts saturation by mixing each channel with luma coefficients before scaling.
 * @param {{ value?: number }} props
 * @param {number} [props.value=0] Saturation adjustment percentage.
 * @returns {JSX.Element}
 */
const SaturationFilter = ({ value = 0 }) => {
  const a = value / 100 + 1;
  const b = 0.3086 * (1 - a),
    c = 0.6094 * (1 - a),
    d = 0.082 * (1 - a);

  return (
    <feColorMatrix
      type="matrix"
      values={[b + a, c, d, 0, 0, b, c + a, d, 0, 0, b, c, d + a, 0, 0, 0, 0, 0, 1, 0].join(' ')}
    />
  );
};

/**
 * Combines multiple filter primitives into a reusable SVG filter definition.
 * @param {{
 *   id: string,
 *   contrast?: number,
 *   brightness?: number,
 *   saturation?: number,
 *   isBW?: boolean
 * }} props
 * @param {string} props.id Filter identifier used by SVG elements.
 * @param {number} [props.contrast] Contrast adjustment percentage.
 * @param {number} [props.brightness] Brightness adjustment percentage.
 * @param {number} [props.saturation] Saturation adjustment percentage.
 * @param {boolean} [props.isBW] Whether to render the image in grayscale.
 * @returns {JSX.Element}
 */
const SVGFilter = ({ id, contrast, brightness, saturation, isBW }) => (
  <filter id={id} colorInterpolationFilters="sRGB">
    <feBlend />
    <ContrastFilter value={contrast} />
    <BrightenFilter value={brightness} />
    {isBW ? null : <SaturationFilter value={saturation} />}
    <BlackWhiteFilter active={isBW} />
  </filter>
);

export default SVGFilter;
